import React from 'react';
import { DeleteOutlineRounded as DeleteIcon } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { COLOR } from '@src/styles/color';
import { formatFileSize } from '@src/utils/file';
import { getFileIcon } from '@src/services/project';
import {
  StyledFileItem,
  StyledFileContent,
  StyledFileIconWrap,
  StyledFileInfo,
  StyledFileName,
  StyledFileDetails,
  StyledFileDetailText,
  StyledDot,
  StyledDeleteBtn,
  StyledLoaderWrap,
} from './index.style';

/**
 * Base file upload item — matches Figma node 13002:6472.
 *
 * @param {File} file — file object (must have `name` and `size`).
 * @param {() => void} [onRemove] — remove handler; omit to hide delete button.
 * @param {(file) => ReactNode} [renderIcon] — override icon (defaults to `getFileIcon`
 *                                             auto-detect by extension).
 * @param {string[]} [details] — detail labels rendered as "a • b • c".
 *                               Defaults to `[formatFileSize(file.size)]`.
 *                               Dots are inserted between items automatically.
 * @param {boolean} [loading=false] — when true, replaces details with spinner + loadingText.
 * @param {string} [loadingText] — text shown next to the spinner (e.g. "Uploading 20%...").
 */
const UploadedFileItem = ({
  file,
  onRemove,
  renderIcon,
  details,
  loading = false,
  loadingText = '',
}) => {
  if (!file) return null;

  const effectiveDetails =
    details ?? [formatFileSize(file.size)].filter(Boolean);

  return (
    <StyledFileItem>
      <StyledFileContent>
        <StyledFileIconWrap>
          {renderIcon ? (
            renderIcon(file)
          ) : (
            <img src={getFileIcon(file)} alt="file-icon" />
          )}
        </StyledFileIconWrap>
        <StyledFileInfo>
          <StyledFileName title={file.name}>{file.name}</StyledFileName>
          <StyledFileDetails>
            {loading ? (
              <>
                <StyledLoaderWrap>
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: COLOR.blueV2 }}
                  />
                </StyledLoaderWrap>
                {loadingText && (
                  <StyledFileDetailText>{loadingText}</StyledFileDetailText>
                )}
              </>
            ) : (
              effectiveDetails.map((detail, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>
                  {idx > 0 && <StyledDot />}
                  <StyledFileDetailText>{detail}</StyledFileDetailText>
                </React.Fragment>
              ))
            )}
          </StyledFileDetails>
        </StyledFileInfo>
      </StyledFileContent>
      {onRemove && (
        <StyledDeleteBtn onClick={onRemove} aria-label="remove file">
          <DeleteIcon />
        </StyledDeleteBtn>
      )}
    </StyledFileItem>
  );
};

export default UploadedFileItem;
